import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  PiggyBank, TrendingUp, Package, Wallet, ArrowUpRight,
  Sparkles, ChevronRight, Bell, Inbox
} from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Tooltip, AreaChart, Area, XAxis } from "recharts";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { formatRupiah, formatDate } from "@/lib/dummyData";
import { useAuth } from "@/lib/auth";
import { SavingsAPI, ShuAPI, OrderAPI, ProductAPI } from "@/lib/api";

// Peta status pesanan backend -> label + warna badge.
const ORDER_STATUS = {
  PENDING: { label: "Menunggu Bayar", c: "bg-stone-soft/40 text-ink/70 hover:bg-stone-soft/40" },
  PAID: { label: "Dibayar", c: "bg-forest/10 text-forest hover:bg-forest/10" },
  PACKED: { label: "Dikemas", c: "bg-amber-warm/20 text-amber-700 hover:bg-amber-warm/20" },
  SHIPPED: { label: "Dikirim", c: "bg-amber-warm/20 text-amber-700 hover:bg-amber-warm/20" },
  COMPLETED: { label: "Selesai", c: "bg-sage/20 text-forest hover:bg-sage/20" },
  CANCELLED: { label: "Dibatalkan", c: "bg-terracotta/10 text-terracotta hover:bg-terracotta/10" },
  COMPLAINT: { label: "Dikomplain", c: "bg-terracotta/10 text-terracotta hover:bg-terracotta/10" },
};

// Label + warna untuk jenis mutasi simpanan (dipakai di panel notifikasi/aktivitas).
const SAVING_LABEL = {
  POKOK: { t: "Simpanan pokok", c: "bg-forest/10 text-forest" },
  WAJIB: { t: "Simpanan wajib", c: "bg-sage/20 text-forest" },
  SUKARELA: { t: "Simpanan sukarela", c: "bg-amber-warm/15 text-amber-700" },
};

export default function MemberDashboard() {
  const { user } = useAuth();
  const [savings, setSavings] = useState(null);      // { savings:{pokok,wajib,sukarela,total}, history:[] }
  const [shu, setShu] = useState(null);              // { history, current, previous, growthPct }
  const [orders, setOrders] = useState([]);
  const [featured, setFeatured] = useState(null);

  useEffect(() => {
    SavingsAPI.mine().then(setSavings).catch(() => {});
    ShuAPI.mine().then(setShu).catch(() => {});
    OrderAPI.mine().then((rows) => setOrders(rows || [])).catch(() => {});
    // Produk unggulan = diskon anggota terbesar dari katalog nyata.
    ProductAPI.list().then((rows) => {
      const best = (rows || [])
        .filter((p) => p.price_non_member > p.price_member)
        .sort((a, b) => (b.price_non_member - b.price_member) - (a.price_non_member - a.price_member))[0];
      setFeatured(best || null);
    }).catch(() => {});
  }, []);

  const acc = savings?.savings || { pokok: 0, wajib: 0, sukarela: 0, total: 0 };
  const shuCurrent = shu?.current ? Number(shu.current.amount) : 0;
  const shuHistory = shu?.history || [];

  // Sparkline saldo simpanan dari riwayat mutasi (balance_after) — kronologis.
  const sparkData = useMemo(() => {
    const h = (savings?.history || []).slice().reverse();
    const pts = h.map((t) => ({ v: Number(t.balance_after) || 0 })).filter((p) => p.v > 0);
    return pts.length ? pts : (acc.total > 0 ? [{ v: 0 }, { v: acc.total }] : []);
  }, [savings, acc.total]);

  // Progress periode SHU tahun berjalan (berbasis tanggal nyata, bukan data statis).
  const yearProgress = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    const end = new Date(now.getFullYear() + 1, 0, 1);
    return Math.round(((now - start) / (end - start)) * 100);
  }, []);

  const activities = (savings?.history || []).slice(0, 4);
  const firstName = (user?.name || "Anggota").split(" ")[0];

  return (
    <div className="space-y-7">
      {/* Greeting */}
      <section className="rounded-3xl bg-forest text-cream p-7 lg:p-9 relative overflow-hidden grain-overlay">
        <div className="relative z-10 grid lg:grid-cols-3 gap-6 items-center">
          <div className="lg:col-span-2">
            <div className="text-xs uppercase tracking-[0.22em] text-amber-warm font-semibold">Selamat datang kembali</div>
            <h1 className="font-display text-3xl lg:text-4xl font-bold mt-2">Halo, {firstName} <span className="font-editorial italic text-amber-warm">👋</span></h1>
            <p className="text-cream/80 mt-2 max-w-lg">
              Anggota
              {user?.koperasi_member_number ? ` · ${user.koperasi_member_number}` : ""}
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/anggota/tabungan"><Button className="rounded-full bg-amber-warm text-ink hover:bg-amber-warm/90" data-testid="member-cta-tabungan">Lihat Tabungan <ArrowUpRight size={15} /></Button></Link>
              <Link to="/anggota/toko"><Button variant="outline" className="rounded-full bg-transparent border-cream/30 text-cream hover:bg-white/10 hover:text-cream">Belanja Anggota</Button></Link>
            </div>
          </div>
          <div className="rounded-2xl bg-white/10 backdrop-blur p-5 border border-white/15">
            <div className="text-[10px] uppercase tracking-[0.22em] text-amber-warm font-semibold">Total Simpanan</div>
            <div className="font-display font-bold text-3xl mt-1.5" data-testid="member-total-savings">{formatRupiah(acc.total)}</div>
            {shu?.growthPct != null && shu.growthPct !== 0 && (
              <div className="text-xs text-cream/75 mt-1 flex items-center gap-1"><TrendingUp size={12} className="text-amber-warm" /> {shu.growthPct > 0 ? "+" : ""}{shu.growthPct}% SHU YoY</div>
            )}
            <div className="h-12 mt-2">
              {sparkData.length > 0 && (
                <ResponsiveContainer>
                  <AreaChart data={sparkData}>
                    <defs><linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#D48C45" stopOpacity={0.7} /><stop offset="100%" stopColor="#D48C45" stopOpacity={0} /></linearGradient></defs>
                    <Area dataKey="v" stroke="#D48C45" fill="url(#g1)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Quick stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiTile label="Simpanan Pokok" value={Number(acc.pokok)} icon={PiggyBank} accent="bg-sage/20 text-forest" />
        <KpiTile label="Simpanan Wajib" value={Number(acc.wajib)} icon={PiggyBank} accent="bg-amber-warm/20 text-amber-700" />
        <KpiTile label="Simpanan Sukarela" value={Number(acc.sukarela)} icon={Wallet} accent="bg-forest/10 text-forest" />
        <KpiTile label={shu?.current ? `SHU ${shu.current.year}` : "Estimasi SHU"} value={shuCurrent} icon={TrendingUp} accent="bg-terracotta/10 text-terracotta" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* SHU progress card */}
        <Card className="lg:col-span-2 rounded-3xl border-stone-soft p-7">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-ink/55 font-semibold">Progress SHU {shu?.current?.year || new Date().getFullYear()}</div>
              <h3 className="font-display text-2xl font-bold text-ink mt-1">{formatRupiah(shuCurrent)}</h3>
              <p className="text-sm text-ink/75 mt-1">{shu?.current ? "Hasil Usaha (SHU) terakhir" : "Belum ada catatan SHU"}</p>
            </div>
            <Link to="/anggota/shu" className="text-sm text-forest font-semibold inline-flex items-center gap-1">Detail <ChevronRight size={14} /></Link>
          </div>
          <Progress value={yearProgress} className="mt-5 h-2.5" />
          <div className="text-xs text-ink/55 mt-2">{yearProgress}% periode tahun berjalan</div>

          <div className="grid grid-cols-2 gap-4 mt-6">
            <div className="rounded-2xl bg-cream p-4">
              <div className="text-[10px] uppercase tracking-wider text-ink/55 font-semibold">Jasa Modal</div>
              <div className="font-display font-bold text-lg text-ink mt-1">{formatRupiah(shu?.current ? Number(shu.current.jasa_modal) : 0)}</div>
            </div>
            <div className="rounded-2xl bg-cream p-4">
              <div className="text-[10px] uppercase tracking-wider text-ink/55 font-semibold">Jasa Usaha</div>
              <div className="font-display font-bold text-lg text-ink mt-1">{formatRupiah(shu?.current ? Number(shu.current.jasa_usaha) : 0)}</div>
            </div>
          </div>

          <div className="h-32 mt-5">
            {shuHistory.length > 0 ? (
              <ResponsiveContainer>
                <LineChart data={shuHistory}>
                  <XAxis dataKey="year" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#889285" }} />
                  <Tooltip content={({ active, payload }) => active && payload?.[0] ? <div className="bg-white border border-stone-soft rounded-lg px-3 py-2 text-xs shadow-md"><div className="font-semibold">{payload[0].payload.year}</div><div className="text-forest">{formatRupiah(payload[0].value)}</div></div> : null} />
                  <Line type="monotone" dataKey="amount" stroke="#2D5A27" strokeWidth={2.5} dot={{ fill: "#D48C45", r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full grid place-items-center text-sm text-ink/45">Riwayat SHU akan tampil setelah pembagian pertama</div>
            )}
          </div>
        </Card>

        {/* Aktivitas simpanan terbaru (nyata dari ledger simpanan) */}
        <Card className="rounded-3xl border-stone-soft p-6">
          <div className="flex items-center gap-2 mb-4">
            <Bell size={16} className="text-amber-warm" />
            <h3 className="font-display font-semibold text-ink">Aktivitas Terbaru</h3>
          </div>
          <div className="space-y-3">
            {activities.length === 0 ? (
              <div className="py-8 text-center text-sm text-ink/45"><Inbox className="mx-auto text-ink/30 mb-2" size={24} />Belum ada aktivitas</div>
            ) : activities.map((a, i) => {
              const meta = SAVING_LABEL[a.type] || { t: a.type, c: "bg-stone-soft/40 text-ink/70" };
              return (
                <div key={i} className="flex gap-3 items-start" data-testid={`member-activity-${i}`}>
                  <Badge className={`${meta.c} border-0 rounded-full text-[10px] uppercase tracking-wider px-2 mt-0.5`}>{formatDate(a.created_at)}</Badge>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-ink">{meta.t} +{formatRupiah(Number(a.amount))}</div>
                    <div className="text-xs text-ink/75">{a.note || `Saldo: ${formatRupiah(Number(a.balance_after))}`}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Recent orders + flash sale */}
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 rounded-3xl border-stone-soft p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold text-ink">Pesanan Terbaru</h3>
            <Link to="/anggota/pesanan" className="text-sm text-forest font-semibold">Semua →</Link>
          </div>
          <div className="space-y-2">
            {orders.length === 0 ? (
              <div className="py-10 text-center text-sm text-ink/45"><Package className="mx-auto text-ink/30 mb-2" size={24} />Belum ada pesanan</div>
            ) : orders.slice(0, 4).map((o) => {
              const st = ORDER_STATUS[o.status] || { label: o.status, c: "bg-stone-soft/40 text-ink/70" };
              return (
                <div key={o.id} className="flex items-center gap-4 py-3 border-b border-stone-soft/60 last:border-0" data-testid={`recent-order-${o.id}`}>
                  <div className="w-10 h-10 rounded-xl bg-cream grid place-items-center"><Package size={17} className="text-forest" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-ink">{o.order_number}</div>
                    <div className="text-xs text-ink/55">{formatDate(o.created_at)} · {Number(o.item_count) || 0} item</div>
                  </div>
                  <Badge className={`border-0 rounded-full text-[10px] uppercase tracking-wider ${st.c}`}>{st.label}</Badge>
                  <div className="font-display font-bold text-sm text-ink">{formatRupiah(Number(o.total))}</div>
                </div>
              );
            })}
          </div>
        </Card>

        {featured && (
          <Card className="rounded-3xl border-stone-soft p-6 bg-amber-warm text-ink relative overflow-hidden">
            <Sparkles className="absolute top-3 right-3 text-ink/30" size={28} />
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold">Hemat untuk Anggota</div>
            <h3 className="font-display text-xl font-bold mt-1 line-clamp-2">{featured.name}</h3>
            <p className="text-sm text-ink/80 mt-1">Hemat {formatRupiah(featured.price_non_member - featured.price_member)} dengan harga khusus anggota.</p>
            <div className="mt-4 flex items-baseline gap-2">
              <div className="font-display font-bold text-2xl">{formatRupiah(featured.price_member)}</div>
              <div className="text-sm line-through text-ink/55">{formatRupiah(featured.price_non_member)}</div>
            </div>
            <Link to="/anggota/toko"><Button className="mt-4 rounded-full bg-ink text-cream hover:bg-ink/90 w-full">Belanja Sekarang</Button></Link>
          </Card>
        )}
      </div>
    </div>
  );
}

const KpiTile = ({ label, value, icon: Icon, accent }) => (
  <Card className="rounded-2xl border-stone-soft p-5 bg-white" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center justify-between mb-3">
      <div className={`w-9 h-9 rounded-lg grid place-items-center ${accent}`}><Icon size={16} /></div>
      <ArrowUpRight size={14} className="text-ink/55" />
    </div>
    <div className="text-[10px] uppercase tracking-[0.16em] text-ink/55 font-semibold">{label}</div>
    <div className="font-display font-bold text-xl text-ink mt-1">{formatRupiah(value)}</div>
  </Card>
);
